import React, { useMemo } from 'react';
import { Box, Paper, Typography, Checkbox } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';

export const JOB_FILTER_DEF = [
  {
    id: 'commitment',
    label: 'สถานะ : ภาระผูกพัน',
    color: '#1e40af',
    autoComputed: true,
    options: [
      { key: 'has', label: 'มีภาระผูกพัน' },
      { key: 'no', label: 'ไม่มีภาระผูกพัน' },
    ],
  },
  {
    id: 'work',
    label: 'สถานะงาน',
    color: '#065f46',
    field: 'dash_work',
    options: [
      { key: 'done', label: 'เสร็จงาน' },
      { key: 'na', label: 'ไม่มี' },
    ],
  },
  {
    id: 'delivery',
    label: 'สถานะ : ส่งงาน',
    color: '#0369a1',
    field: 'dash_delivery',
    options: [
      { key: 'none', label: 'ยังไม่ได้ส่งงาน' },
      { key: 'partial', label: 'ส่งงาน (บางส่วน)' },
      { key: 'done', label: 'ส่งงานแล้ว' },
      { key: 'na', label: 'ไม่มี' },
    ],
  },
  {
    id: 'disbursement',
    label: 'การเบิกจ่าย',
    color: '#0f766e',
    field: 'dash_disbursement',
    options: [
      { key: 'has', label: 'มี' },
      { key: 'none', label: 'ไม่มี' },
    ],
  },
  {
    id: 'warranty',
    label: 'Warranty/Guarantee',
    color: '#7c3aed',
    field: 'dash_warranty',
    options: [
      { key: 'warranty', label: 'Warranty' },
      { key: 'expired', label: 'ครบกำหนด' },
      { key: 'none', label: 'ไม่มี' },
      { key: 'no_data', label: 'ไม่มีข้อมูล' },
    ],
  },
  {
    id: 'billing',
    label: 'สถานะ : วางบิล',
    color: '#be123c',
    field: 'dash_billing',
    options: [
      { key: 'none', label: 'ยังไม่วางบิล' },
      { key: 'partial', label: 'วางบิล (บางส่วน)' },
      { key: 'done', label: 'วางบิลแล้ว' },
      { key: 'na', label: 'ไม่มี' },
    ],
  },
  {
    id: 'payment',
    label: 'สถานะ : รับเงิน',
    color: '#b45309',
    field: 'dash_payment',
    options: [
      { key: 'waiting', label: 'รอรับเงิน' },
      { key: 'partial', label: 'รอรับบางส่วน' },
      { key: 'received', label: 'รับแล้วทั้งหมด' },
      { key: 'na', label: 'ไม่มี' },
      { key: 'no_data', label: 'ไม่มีข้อมูล' },
    ],
  },
];

export const FILTER_DEFAULTS = {
  commitment:   { has: true, no: true },
  work:         { done: true, na: true },
  delivery:     { none: true, partial: true, done: true, na: true },
  disbursement: { has: true, none: true },
  warranty:     { warranty: true, expired: true, none: true, no_data: true },
  billing:      { none: true, partial: true, done: true, na: true },
  payment:      { waiting: true, partial: true, received: true, na: true, no_data: true },
};

export function computeCommitment(work, delivery, disbursement, warranty, billing, payment) {
  if (!work && !delivery && !disbursement && !warranty && !billing && !payment) return '';
  const workOk         = !work         || ['done', 'na'].includes(work);
  const deliveryOk     = !delivery     || ['done', 'na'].includes(delivery);
  const disbursementOk = !disbursement || ['none', 'na'].includes(disbursement);
  const warrantyOk     = !warranty     || ['none', 'expired', 'no_data'].includes(warranty);
  const billingOk      = !billing      || ['done', 'na'].includes(billing);
  const paymentOk      = !payment      || ['received', 'na'].includes(payment);
  return (workOk && deliveryOk && disbursementOk && warrantyOk && billingOk && paymentOk) ? 'no' : 'has';
}

export function getRowCategories(row) {
  const progress = row.progress ?? 0;
  const received = row.received ?? 0;
  const billedTotal = row.billed_total ?? 0;
  const po = row.include_vat ? (row.cost || 0) - (row.vat || 0) : (row.cost || 0);
  const work         = row.dash_work         || '';
  const delivery     = row.dash_delivery     || (progress >= 100 ? 'done' : progress > 0 ? 'partial' : 'none');
  const disbursement = row.dash_disbursement || ((row.expense_count ?? 0) > 0 ? 'has' : 'none');
  const warranty     = row.dash_warranty     || (!row.operation_status ? 'no_data' : row.operation_status === 'Warranty' ? 'warranty' : 'none');
  const billing      = row.dash_billing      || (!po ? 'na' : billedTotal >= po ? 'done' : billedTotal > 0 ? 'partial' : 'none');
  const payment      = row.dash_payment      || (!po ? 'no_data' : received >= po ? 'received' : received > 0 ? 'partial' : 'waiting');
  const commitment   = row.dash_commitment   || computeCommitment(work, delivery, disbursement, warranty, billing, payment) || (row.operation_status === 'COMPLETED' ? 'no' : 'has');
  return { commitment, work, delivery, disbursement, warranty, billing, payment };
}

export default function JobFilterPanel({ rows, filters, onFilterChange }) {
  const counts = useMemo(() => {
    const c = {};
    JOB_FILTER_DEF.forEach((g) => {
      c[g.id] = {};
      g.options.forEach((o) => { c[g.id][o.key] = 0; });
    });
    rows.forEach((row) => {
      const cats = getRowCategories(row);
      JOB_FILTER_DEF.forEach((g) => {
        const cat = cats[g.id];
        if (c[g.id][cat] !== undefined) c[g.id][cat]++;
      });
    });
    return c;
  }, [rows]);

  const toggleAll = (groupId) => {
    const group = JOB_FILTER_DEF.find((g) => g.id === groupId);
    const allChecked = group.options.every((o) => filters[groupId][o.key]);
    group.options.forEach((o) => onFilterChange(groupId, o.key, !allChecked));
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1.5,
        p: 1.5,
        borderColor: 'divider',
        borderRadius: 2,
        background: 'linear-gradient(135deg, #fafbff 0%, #f4f6fc 100%)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.8 }}>
        <TuneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.68rem', letterSpacing: 0.6, textTransform: 'uppercase' }}
        >
          ข้อมูลงาน (JOB)
        </Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.75 }}>
        {JOB_FILTER_DEF.map((group) => (
          <Box
            key={group.id}
            sx={{
              p: 1,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: `${group.color}28`,
              bgcolor: '#fff',
              boxShadow: `inset 3px 0 0 ${group.color}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                fontSize: '0.67rem',
                color: group.color,
                borderBottom: `1px solid ${group.color}25`,
                pb: 0.4,
                mb: 0.6,
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { opacity: 0.75 },
              }}
              onClick={() => toggleAll(group.id)}
            >
              {group.label}
            </Typography>

            {group.options.map((opt) => {
              const count = counts[group.id]?.[opt.key] ?? 0;
              const checked = filters[group.id]?.[opt.key] ?? true;
              return (
                <Box
                  key={opt.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.2,
                    py: 0.1,
                    px: 0.3,
                    cursor: 'pointer',
                    borderRadius: 0.8,
                    transition: 'background 0.1s',
                    '&:hover': { bgcolor: `${group.color}0c` },
                  }}
                  onClick={() => onFilterChange(group.id, opt.key, !checked)}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={() => {}}
                    disableRipple
                    sx={{
                      p: 0.2,
                      color: `${group.color}80`,
                      '&.Mui-checked': { color: group.color },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.71rem',
                      flexGrow: 1,
                      color: checked ? 'text.primary' : 'text.disabled',
                      lineHeight: 1.4,
                      userSelect: 'none',
                    }}
                  >
                    {opt.label}
                  </Typography>
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 17,
                      borderRadius: 10,
                      bgcolor: checked ? group.color : '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 0.7,
                      flexShrink: 0,
                      transition: 'background 0.15s',
                    }}
                  >
                    <Typography
                      sx={{ fontSize: '0.6rem', fontWeight: 700, color: checked ? '#fff' : '#94a3b8', lineHeight: 1, userSelect: 'none' }}
                    >
                      {count}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
